import { colorToCss, darken } from "@omnia/shared/color/color";
import { CropId, CropStyleFillPattern } from "../../generated/enums";

const cropFillPatterns = {
    [CropId.Woodland]: "woodland.png",
};

/**
 * Returns the name of a fill pattern associated with a crop
 * @param cropId Crop id
 * @returns Pattern name
 */
export function getCropPatternName(cropId: CropId) {
    return cropFillPatterns[cropId || 0];
}

/**
 * Used as a cache by loadImgPattern
 */
const loadedPatterns: { [key: string]: CanvasPattern } = {};

/**
 * Loads a file-based pattern
 * @param name Name of the pattern - should match an existing file in /images/map/patterns
 * @param callback Callback called when the patterns has loaded
 */
export function loadImgPattern(name: string, callback?: (pattern: CanvasPattern) => void) {
    let pattern = loadedPatterns[name];

    if (pattern !== undefined) {
        return pattern;
    }

    loadedPatterns[name] = null;

    const img = new Image();

    img.onload = () => {
        const canvas = document.createElement("canvas");

        const ratio = window.devicePixelRatio;

        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        const context = canvas.getContext("2d");

        context.drawImage(img, 0, 0, canvas.width, canvas.height);

        loadedPatterns[name] = context.createPattern(canvas, "repeat");

        callback && callback(pattern);
    };

    img.src = "/images/map/patterns/" + name;
}

const cropStylePatterns: { [key: string]: CanvasPattern } = {};

export function getOrCreateCropStylePattern(color: number[], pattern: CropStyleFillPattern, opacity: number) {
    const key = `${colorToCss(color)}/${pattern}/${opacity}`;

    let p = cropStylePatterns[key];

    if (!p) {
        p = createCropStylePattern(color, pattern, opacity);
        cropStylePatterns[key] = p;
    }

    return p;
}

const cropStylePatternRules = {
    [CropStyleFillPattern.RightDiagonal]: [
        [0, 1],
        [1, 0],
    ],

    [CropStyleFillPattern.LeftDiagonal]: [
        [0, 0],
        [1, 1],
    ],

    [CropStyleFillPattern.Horizontal]: [
        [0, 0.5],
        [1, 0.5],
    ],

    [CropStyleFillPattern.Vertical]: [
        [0.5, 0],
        [0.5, 1],
    ],
};

export function createCropStylePattern(color: number[], pattern: CropStyleFillPattern, opacity: number) {
    const rules: (typeof cropStylePatternRules)[CropStyleFillPattern.Vertical] = cropStylePatternRules[pattern || 0];

    if (!rules) {
        return undefined;
    }

    const getPoint = (p: number[]) => {
        return [p[0] * width, p[1] * height];
    };

    const cnv = document.createElement("canvas");

    const width = 10;
    const height = 10;

    cnv.width = width;
    cnv.height = height;

    const ctx = cnv.getContext("2d");

    ctx.fillStyle = colorToCss(color, opacity);
    ctx.fillRect(0, 0, width, height);

    ctx.strokeStyle = colorToCss(darken(color, 0.5), 1);

    ctx.beginPath();

    let [x, y] = getPoint(rules[0]);
    ctx.moveTo(x, y);

    for (const p of rules.slice(1)) {
        const [x, y] = getPoint(p);
        ctx.lineTo(x, y);
    }

    ctx.stroke();

    return ctx.createPattern(cnv, "repeat");
}
